export interface DimensionProperty {
  key: string;
  label: string;
  dataType: string;
  required: boolean;
  unique?: boolean;
  description?: string;
  enumValues?: string[];
  enumLocked?: boolean;
  origin: 'STANDARD' | 'CUSTOM';
  isCustom: boolean;
  isActive: boolean;
  isSystemField?: boolean;
  readOnly?: boolean;
}

export interface ResolvedDimension {
  dimensionKey: string;
  name: string;
  description?: string;
  icon?: string;
  industries: string[];
  entityOrigin: 'SYSTEM' | 'CUSTOM';
  isCustomEntity: boolean;
  properties: DimensionProperty[];
  propertyCounts: { standard: number; custom: number; total: number };
}

function p(
  key: string,
  label: string,
  dataType: string,
  extra: Partial<DimensionProperty> = {}
): DimensionProperty {
  return {
    key,
    label,
    dataType,
    required: extra.required ?? false,
    unique: extra.unique,
    description: extra.description,
    enumValues: extra.enumValues,
    enumLocked: extra.enumLocked ?? (dataType === 'enum' ? true : undefined),
    origin: 'STANDARD',
    isCustom: false,
    isActive: true,
  };
}

function entity(
  dimensionKey: string,
  name: string,
  description: string,
  industries: string[],
  properties: DimensionProperty[]
): ResolvedDimension {
  const businessProps = properties;
  const allProps = [...SYSTEM_RECORD_PROPERTIES, ...businessProps];
  return {
    dimensionKey,
    name,
    description,
    icon: dimensionKey,
    industries,
    entityOrigin: 'SYSTEM',
    isCustomEntity: false,
    properties: allProps,
    propertyCounts: {
      standard: allProps.filter((prop) => !prop.isCustom).length,
      custom: 0,
      total: allProps.length,
    },
  };
}

/** System envelope fields on every master record — website-local (avoids Vite CJS import from shared-types). */
const MASTER_RECORD_SYSTEM_PROPERTIES: DimensionProperty[] = [
  {
    key: 'recordId',
    label: 'Record ID',
    dataType: 'string',
    required: true,
    unique: true,
    description: 'Internally generated UUID. Assigned at creation; immutable.',
    origin: 'STANDARD',
    isCustom: false,
    isActive: true,
    isSystemField: true,
    readOnly: true,
  },
  {
    key: 'createdOn',
    label: 'Created On',
    dataType: 'datetime',
    required: true,
    description: 'Timestamp when the record was created.',
    origin: 'STANDARD',
    isCustom: false,
    isActive: true,
    isSystemField: true,
    readOnly: true,
  },
  {
    key: 'lastUpdatedOn',
    label: 'Last Updated On',
    dataType: 'datetime',
    required: true,
    description: 'Timestamp when the record was last modified.',
    origin: 'STANDARD',
    isCustom: false,
    isActive: true,
    isSystemField: true,
    readOnly: true,
  },
  {
    key: 'createdBy',
    label: 'Created By',
    dataType: 'string',
    required: false,
    description: 'User who created the record.',
    origin: 'STANDARD',
    isCustom: false,
    isActive: true,
    isSystemField: true,
    readOnly: true,
  },
  {
    key: 'lastUpdatedBy',
    label: 'Last Updated By',
    dataType: 'string',
    required: false,
    description: 'User who last modified the record.',
    origin: 'STANDARD',
    isCustom: false,
    isActive: true,
    isSystemField: true,
    readOnly: true,
  },
];

const SYSTEM_RECORD_PROPERTIES: DimensionProperty[] = MASTER_RECORD_SYSTEM_PROPERTIES.map((prop) => ({
  ...prop,
  origin: 'STANDARD',
  isCustom: false,
}));

const TL = ['TRANSPORTATION', 'LOGISTICS'];
const TLG = ['TRANSPORTATION', 'LOGISTICS', 'GENERAL'];

export const FALLBACK_DIMENSIONS: ResolvedDimension[] = [
  entity('driver', 'Driver', 'Licensed driver identity, credentials, and employment attributes.', TL, [
    p('driverId', 'Driver ID', 'string', { required: true, unique: true, description: 'Primary driver identifier' }),
    p('displayName', 'Display Name', 'string', { required: true }),
    p('firstName', 'First Name', 'string'),
    p('lastName', 'Last Name', 'string'),
    p('email', 'Email', 'string'),
    p('phone', 'Phone', 'string'),
    p('employeeId', 'Employee ID', 'string', { unique: true }),
    p('licenseNumber', 'License Number', 'string'),
    p('licenseState', 'License State / Province', 'string'),
    p('licenseClass', 'License Class', 'string'),
    p('licenseExpiry', 'License Expiry', 'date'),
    p('cdlEndorsements', 'CDL Endorsements', 'array'),
    p('hireDate', 'Hire Date', 'date'),
    p('homeTerminal', 'Home Terminal', 'string'),
    p('status', 'Status', 'enum', { enumValues: ['active', 'inactive', 'suspended', 'terminated'] }),
    p('hosExempt', 'HOS Exempt', 'boolean'),
  ]),
  entity('vehicle', 'Vehicle', 'Power unit / asset identity used across telematics, compliance, and maintenance.', TL, [
    p('vehicleId', 'Vehicle ID', 'string', { required: true, unique: true }),
    p('displayName', 'Display Name', 'string', { required: true }),
    p('vin', 'VIN', 'string', { unique: true, description: 'Vehicle identification number' }),
    p('unitNumber', 'Unit Number', 'string'),
    p('plate', 'License Plate', 'string'),
    p('plateState', 'Plate State', 'string'),
    p('make', 'Make', 'string'),
    p('model', 'Model', 'string'),
    p('year', 'Year', 'number'),
    p('vehicleType', 'Vehicle Type', 'enum', { enumValues: ['tractor', 'straight_truck', 'van', 'trailer', 'reefer', 'other'] }),
    p('fuelType', 'Fuel Type', 'enum', { enumValues: ['diesel', 'gasoline', 'cng', 'lng', 'electric', 'hybrid', 'other'] }),
    p('gvwr', 'GVWR', 'number', { description: 'Gross vehicle weight rating' }),
    p('fleetId', 'Fleet ID', 'string'),
    p('registrationState', 'Registration State', 'string'),
    p('status', 'Status', 'enum', { enumValues: ['active', 'inactive', 'out_of_service', 'sold'] }),
  ]),
  entity('fleet', 'Fleet', 'Operating group of vehicles and drivers.', TL, [
    p('fleetId', 'Fleet ID', 'string', { required: true, unique: true }),
    p('name', 'Fleet Name', 'string', { required: true }),
    p('region', 'Region', 'string'),
    p('dispatcherId', 'Dispatcher ID', 'string'),
    p('status', 'Status', 'enum', { enumValues: ['active', 'inactive'] }),
  ]),
  entity('location', 'Location', 'Yards, terminals, customer sites, and geofenced places.', TLG, [
    p('locationId', 'Location ID', 'string', { required: true, unique: true }),
    p('name', 'Name', 'string', { required: true }),
    p('locationType', 'Location Type', 'enum', { enumValues: ['terminal', 'yard', 'customer', 'warehouse', 'geofence', 'other'] }),
    p('address', 'Address', 'string'),
    p('city', 'City', 'string'),
    p('state', 'State / Province', 'string'),
    p('postalCode', 'Postal Code', 'string'),
    p('country', 'Country', 'string'),
    p('latitude', 'Latitude', 'number'),
    p('longitude', 'Longitude', 'number'),
    p('status', 'Status', 'enum', { enumValues: ['active', 'inactive'] }),
  ]),
  entity('route', 'Route', 'Planned path between origin and destination.', TL, [
    p('routeId', 'Route ID', 'string', { required: true, unique: true }),
    p('name', 'Route Name', 'string', { required: true }),
    p('originLocationId', 'Origin Location ID', 'string'),
    p('destinationLocationId', 'Destination Location ID', 'string'),
    p('distanceMiles', 'Distance (miles)', 'number'),
    p('plannedDurationMinutes', 'Planned Duration (minutes)', 'number'),
    p('status', 'Status', 'enum', { enumValues: ['active', 'inactive', 'archived'] }),
  ]),
  entity('load', 'Load', 'Freight shipment / load master identity.', TL, [
    p('loadId', 'Load ID', 'string', { required: true, unique: true }),
    p('reference', 'Reference', 'string'),
    p('customerId', 'Customer ID', 'string'),
    p('origin', 'Origin', 'string'),
    p('destination', 'Destination', 'string'),
    p('scheduledPickup', 'Scheduled Pickup', 'datetime'),
    p('scheduledDelivery', 'Scheduled Delivery', 'datetime'),
    p('status', 'Status', 'enum', { enumValues: ['planned', 'dispatched', 'in_transit', 'delivered', 'cancelled'] }),
  ]),
  entity('customer', 'Customer', 'Shipper / consignee / billing account.', TLG, [
    p('customerId', 'Customer ID', 'string', { required: true, unique: true }),
    p('customerName', 'Customer Name', 'string', { required: true }),
    p('accountNumber', 'Account Number', 'string'),
    p('billingAddress', 'Billing Address', 'string'),
    p('contactEmail', 'Contact Email', 'string'),
    p('contactPhone', 'Contact Phone', 'string'),
    p('status', 'Status', 'enum', { enumValues: ['active', 'inactive'] }),
  ]),
  entity('partner', 'Partner', 'Carriers, brokers, and other operating partners.', TL, [
    p('partnerId', 'Partner ID', 'string', { required: true, unique: true }),
    p('partnerName', 'Partner Name', 'string', { required: true }),
    p('partnerType', 'Partner Type', 'enum', { enumValues: ['carrier', 'broker', 'shipper', 'vendor', 'other'] }),
    p('scac', 'SCAC', 'string'),
    p('dotNumber', 'DOT Number', 'string'),
    p('mcNumber', 'MC Number', 'string'),
    p('status', 'Status', 'enum', { enumValues: ['active', 'inactive'] }),
  ]),
  entity('warehouse', 'Warehouse', 'Distribution and storage facilities.', ['LOGISTICS'], [
    p('warehouseId', 'Warehouse ID', 'string', { required: true, unique: true }),
    p('name', 'Name', 'string', { required: true }),
    p('locationId', 'Location ID', 'string'),
    p('capacitySqFt', 'Capacity (sq ft)', 'number'),
    p('status', 'Status', 'enum', { enumValues: ['active', 'inactive'] }),
  ]),
  entity('contact', 'Contact', 'Person associated with an account or site.', ['GENERAL'], [
    p('contactId', 'Contact ID', 'string', { required: true, unique: true }),
    p('displayName', 'Display Name', 'string', { required: true }),
    p('email', 'Email', 'string'),
    p('phone', 'Phone', 'string'),
    p('title', 'Title', 'string'),
    p('accountId', 'Account ID', 'string'),
    p('status', 'Status', 'enum', { enumValues: ['active', 'inactive'] }),
  ]),
];

export interface ListResponse {
  companyId: string;
  industry: string;
  industries: string[];
  dimensions: ResolvedDimension[];
}

export function fallbackListResponse(): ListResponse {
  return {
    companyId: '',
    industry: 'TRANSPORTATION',
    industries: ['TRANSPORTATION', 'LOGISTICS', 'HEALTHCARE', 'GENERAL'],
    dimensions: FALLBACK_DIMENSIONS,
  };
}
